import { ResidenceOwnership } from '@servicetitan/financing-api-client';

/**
 * Declares the set of values a provider/flow accepts for an enumerated field, plus the value an
 * out-of-set input collapses to when projected onto that provider's API.
 *
 * This is intended to be the single source of truth for a provider-specific field value domain:
 * the form options, the validation schema, and the outbound sanitization should all derive from
 * the same FieldValueSet rather than re-encoding the rule in each place.
 *
 * `allowed` is ordered for display and lists only user-selectable values; the `fallback` (e.g.
 * `NotSet`) is a submission sentinel, never an option.
 */
export interface FieldValueSet<T extends string = string> {
    // User-selectable values this provider/flow accepts for the field, in display order.
    allowed: readonly T[];
    // Value any non-allowed input collapses to when sent to this provider.
    fallback: T;
}

/**
 * First-look residence-ownership domain.
 *
 * First-look DTOs (financing-api `ResidenceOwnership2` and prequals-api `ResidenceOwnership`) only
 * model these values. Second-look-only values (e.g. Finturf's `Other`) are collapsed to `Rent`
 * before reaching any first-look API — the closest semantic equivalent for someone who does not
 * hold a mortgage or own the property outright.
 */
export const FIRST_LOOK_RESIDENCE_OWNERSHIP: FieldValueSet<ResidenceOwnership> = {
    allowed: [ResidenceOwnership.OwnWithMortgage, ResidenceOwnership.Own, ResidenceOwnership.Rent],
    fallback: ResidenceOwnership.Rent,
};

/**
 * Resolves the active residence-ownership value set for the current flow.
 *
 * The domain is widened beyond the first-look default only when a second-look provider whose config
 * declares a `residenceType` value set will receive the submission (the waterfall). Passing that
 * provider's config (or `undefined` when no such waterfall applies) is the single place that decides
 * the active domain, and the form options and validation schema both derive from it, keeping them in
 * lockstep. Note this is driven entirely by the second-look provider's config — no provider name is
 * hardcoded here.
 */
export const resolveResidenceOwnershipValueSet = (secondLookProviderConfig?: {
    fieldValueSets?: { residenceType?: FieldValueSet<ResidenceOwnership> };
}): FieldValueSet<ResidenceOwnership> =>
    secondLookProviderConfig?.fieldValueSets?.residenceType ?? FIRST_LOOK_RESIDENCE_OWNERSHIP;
